import { useState, useEffect, useCallback } from "react";
import {
  User,
  Mail,
  Phone,
  Building2,
  ShieldAlert,
  ShieldCheck,
  CheckCircle2,
  Calendar,
  Clock,
  KeyRound,
  Pencil,
  AlertCircle,
  X,
  Lock,
  Layers,
  RotateCw,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { formatRole, isSuperAdmin, isAdmin } from "../../lib/permissions";
import PasswordInput from "../ui/PasswordInput";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function roleTheme(role) {
  const r = (role || "user").toLowerCase().trim();
  if (r === "super_admin") {
    return {
      label: "SUPER ADMIN",
      badgeBg: "#f3e8ff",
      badgeColor: "#6d28d9",
      badgeBorder: "#d8b4fe",
      avatarBg: "#7c3aed",
      avatarColor: "#ffffff",
      icon: ShieldAlert,
      desc: "Full unrestricted access across all workspaces, users, security, settings, and workflows.",
    };
  }
  if (r === "admin") {
    return {
      label: "ADMIN",
      badgeBg: "#dbeafe",
      badgeColor: "#1d4ed8",
      badgeBorder: "#93c5fd",
      avatarBg: "#2563eb",
      avatarColor: "#ffffff",
      icon: ShieldCheck,
      desc: "Managerial operational access across all operational modules, team oversight, and workflow approvals.",
    };
  }
  return {
    label: "USER",
    badgeBg: "#ecfdf5",
    badgeColor: "#047857",
    badgeBorder: "#a7f3d0",
    avatarBg: "#059669",
    avatarColor: "#ffffff",
    icon: User,
    desc: "Operational team access. Daily tasks, queue views, and execution are driven by assigned department.",
  };
}

export default function Profile({ user, onUserUpdated, onNavigate }) {
  const [profile, setProfile] = useState(user || null);
  const [loading, setLoading] = useState(!user);
  const [error, setError] = useState("");

  // Edit Profile Modal
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editBusy, setEditBusy] = useState(false);
  const [editError, setEditError] = useState("");

  // Change Password Modal
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);
  const [pwdError, setPwdError] = useState("");

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      // Use self-service profile endpoint /api/users/me
      const res = await api("get", "/users/me");
      if (res.data) {
        setProfile(res.data);
        if (onUserUpdated) onUserUpdated(res.data);
      }
    } catch (err) {
      console.error("Failed to load user profile:", err);
      setError(err.response?.data?.detail || "Could not retrieve your profile. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [onUserUpdated]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const openEditModal = () => {
    setEditName(profile?.name || "");
    setEditPhone(profile?.phone || "");
    setEditError("");
    setEditOpen(true);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!editName.trim() || editName.trim().length < 2) {
      setEditError("Full name must be at least 2 characters long.");
      return;
    }

    setEditBusy(true);
    setEditError("");

    try {
      const res = await api("patch", "/users/me", {
        name: editName.trim(),
        phone: editPhone.trim(),
      });

      const updated = res.data;
      setProfile(updated);
      if (onUserUpdated) onUserUpdated(updated);
      toast.success("Profile updated successfully!");
      setEditOpen(false);
    } catch (err) {
      setEditError(err.response?.data?.detail || "Failed to update profile. Please try again.");
    } finally {
      setEditBusy(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPwdError("");

    if (!pwdForm.current) {
      setPwdError("Current password is required.");
      return;
    }
    if (pwdForm.next.length < 6) {
      setPwdError("New password must be at least 6 characters long.");
      return;
    }
    if (pwdForm.next !== pwdForm.confirm) {
      setPwdError("New passwords do not match.");
      return;
    }

    setPwdBusy(true);
    try {
      await api("post", "/auth/change-password", {
        current_password: pwdForm.current,
        new_password: pwdForm.next,
      });

      toast.success("Password updated successfully!");
      setPwdForm({ current: "", next: "", confirm: "" });
      setPwdOpen(false);
    } catch (err) {
      setPwdError(err.response?.data?.detail || "Failed to change password. Please verify current password.");
    } finally {
      setPwdBusy(false);
    }
  };

  const currentRole = profile?.role || user?.role || "user";
  const theme = roleTheme(currentRole);
  const RoleIcon = theme.icon;

  const initials = (profile?.name || user?.name || "U")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const depts = Array.isArray(profile?.departments) && profile.departments.length > 0
    ? profile.departments
    : [profile?.department || "Sales"];

  const comps = Array.isArray(profile?.companies) && profile.companies.length > 0
    ? profile.companies
    : [profile?.company || "Pinaki Solutions"];

  if (loading && !profile) {
    return (
      <div className="content" data-testid="profile-page-loading">
        <div style={{ maxWidth: 1040, margin: "0 auto", padding: "40px 0" }}>
          <div className="skeleton-line" style={{ height: 28, width: 220, marginBottom: 12 }} />
          <div className="skeleton-line" style={{ height: 16, width: 340, marginBottom: 30 }} />
          <div
            style={{
              height: 240,
              background: "#fff",
              borderRadius: 14,
              border: "1px solid var(--line)",
              marginBottom: 24,
            }}
          />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
            <div style={{ height: 260, background: "#fff", borderRadius: 12, border: "1px solid var(--line)" }} />
            <div style={{ height: 260, background: "#fff", borderRadius: 12, border: "1px solid var(--line)" }} />
          </div>
        </div>
      </div>
    );
  }

  if (error && !profile) {
    return (
      <div className="content" data-testid="profile-page-error">
        <div style={{ maxWidth: 640, margin: "60px auto", textAlign: "center" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "#fee2e2",
              color: "#dc2626",
              display: "grid",
              placeItems: "center",
              margin: "0 auto 16px",
            }}
          >
            <AlertCircle size={28} />
          </div>
          <h2 style={{ fontSize: 20, color: "var(--navy)", marginBottom: 8 }}>Unable to Load Profile</h2>
          <p style={{ color: "var(--muted)", fontSize: 13, marginBottom: 20 }}>{error}</p>
          <button className="primary-btn" onClick={loadProfile}>
            <RotateCw size={15} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content profile-module" data-testid="profile-page">
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        {/* Page Heading */}
        <div className="page-heading" style={{ marginBottom: 20 }}>
          <div>
            <div className="small-label" style={{ marginBottom: 4 }}>MY ACCOUNT</div>
            <h1 style={{ margin: 0 }}>My Profile</h1>
            <p className="muted" style={{ marginTop: 4 }}>
              Manage your personal credentials, organizational assignments, and workspace security.
            </p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button
              className="outline-btn"
              data-testid="profile-change-password-btn"
              onClick={() => {
                setPwdForm({ current: "", next: "", confirm: "" });
                setPwdError("");
                setPwdOpen(true);
              }}
            >
              <KeyRound size={15} /> Change Password
            </button>
            <button
              className="primary-btn"
              data-testid="profile-edit-btn"
              onClick={openEditModal}
            >
              <Pencil size={15} /> Edit Profile
            </button>
          </div>
        </div>

        {/* Profile Hero Header Card */}
        <div
          className="profile-hero-card"
          data-testid="profile-hero-card"
          style={{
            background: "#ffffff",
            borderRadius: 14,
            border: "1px solid var(--line)",
            boxShadow: "0 4px 20px rgba(15, 23, 42, 0.05)",
            padding: "28px 32px",
            marginBottom: 24,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Subtle decorative background glow */}
          <div
            style={{
              position: "absolute",
              top: -60,
              right: -60,
              width: 220,
              height: 220,
              borderRadius: "50%",
              background: `radial-gradient(circle, ${theme.badgeBg} 0%, rgba(255,255,255,0) 70%)`,
              pointerEvents: "none",
            }}
          />

          <div style={{ display: "flex", alignItems: "center", gap: 24, flexWrap: "wrap", position: "relative" }}>
            {/* Avatar */}
            <div
              className="profile-avatar-large"
              style={{
                width: 76,
                height: 76,
                borderRadius: "50%",
                background: theme.avatarBg,
                color: theme.avatarColor,
                display: "grid",
                placeItems: "center",
                fontSize: 26,
                fontWeight: 800,
                fontFamily: "Plus Jakarta Sans, sans-serif",
                boxShadow: "0 6px 16px rgba(15, 23, 42, 0.16)",
                border: "3px solid #ffffff",
                flexShrink: 0,
              }}
            >
              {profile?.profile_image ? (
                <img
                  src={profile.profile_image}
                  alt={profile.name}
                  style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
                />
              ) : (
                initials
              )}
            </div>

            {/* Identity Info */}
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, color: "var(--navy)" }}>
                  {profile?.name}
                </h2>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 800,
                    letterSpacing: 0.5,
                    background: theme.badgeBg,
                    color: theme.badgeColor,
                    border: `1px solid ${theme.badgeBorder}`,
                  }}
                >
                  <RoleIcon size={12} />
                  {theme.label}
                </span>

                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    background: profile?.is_active !== false ? "#f0fdf4" : "#fef2f2",
                    color: profile?.is_active !== false ? "#166534" : "#991b1b",
                    border: `1px solid ${profile?.is_active !== false ? "#bbf7d0" : "#fecaca"}`,
                  }}
                >
                  <span
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      background: profile?.is_active !== false ? "#22c55e" : "#ef4444",
                    }}
                  />
                  {profile?.is_active !== false ? "Active Account" : "Deactivated"}
                </span>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 18, flexWrap: "wrap", color: "var(--muted)", fontSize: 13 }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Mail size={14} style={{ color: "#64748b" }} />
                  {profile?.email}
                </span>
                {profile?.phone && (
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <Phone size={14} style={{ color: "#64748b" }} />
                    {profile.phone}
                  </span>
                )}
                <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  <Building2 size={14} style={{ color: "#64748b" }} />
                  {comps[0]}
                </span>
              </div>
            </div>

            {/* Quick Stats Pill */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                justifyContent: "center",
                paddingLeft: 20,
                borderLeft: "1px solid #f1f5f9",
              }}
            >
              <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>
                User Identifier
              </div>
              <div
                style={{
                  fontFamily: "DM Mono, monospace",
                  fontSize: 13,
                  fontWeight: 600,
                  color: "var(--navy)",
                  marginTop: 2,
                }}
              >
                {profile?.user_id}
              </div>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20, marginBottom: 24 }}>
          {/* Card 1: Personal Information */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: 12,
              border: "1px solid var(--line)",
              padding: "24px 26px",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.03)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 18, borderBottom: "1px solid #f1f5f9", paddingBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <User size={16} style={{ color: "var(--blue)" }} />
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>Personal Information</h3>
              </div>
              <button
                onClick={openEditModal}
                style={{ background: "none", border: 0, color: "var(--blue)", fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 4, cursor: "pointer" }}
              >
                <Pencil size={12} /> Edit
              </button>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label className="dispatch-label" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>Full Name</label>
                <div style={{ fontSize: 14, fontWeight: 600, color: "var(--navy)" }}>{profile?.name}</div>
              </div>

              <div>
                <label className="dispatch-label" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>
                  Email Address
                  <span style={{ marginLeft: 6, fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>(Managed by Admin)</span>
                </label>
                <div style={{ fontSize: 13.5, color: "var(--navy)", display: "flex", alignItems: "center", gap: 6 }}>
                  <span>{profile?.email}</span>
                  <Lock size={12} style={{ color: "#94a3b8" }} title="Primary email can only be changed by an administrator" />
                </div>
              </div>

              <div>
                <label className="dispatch-label" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>Contact Phone</label>
                <div style={{ fontSize: 13.5, color: profile?.phone ? "var(--navy)" : "#94a3b8" }}>
                  {profile?.phone || "No phone number added"}
                </div>
              </div>

              <div>
                <label className="dispatch-label" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>System Account ID</label>
                <div style={{ fontFamily: "DM Mono, monospace", fontSize: 12.5, color: "#334155" }}>
                  {profile?.user_id}
                </div>
              </div>
            </div>
          </div>

          {/* Card 2: Organizational Assignments */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: 12,
              border: "1px solid var(--line)",
              padding: "24px 26px",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.03)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, borderBottom: "1px solid #f1f5f9", paddingBottom: 12 }}>
              <Layers size={16} style={{ color: "var(--blue)" }} />
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>Organizational Assignments</h3>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label className="dispatch-label" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>System Role</label>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                  <span
                    style={{
                      padding: "3px 8px",
                      borderRadius: 6,
                      fontSize: 11.5,
                      fontWeight: 700,
                      background: theme.badgeBg,
                      color: theme.badgeColor,
                    }}
                  >
                    {theme.label}
                  </span>
                </div>
                <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 4, lineHeight: 1.4 }}>
                  {theme.desc}
                </div>
              </div>

              <div>
                <label className="dispatch-label" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>
                  Assigned Department(s)
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {depts.map((d) => (
                    <span
                      key={d}
                      style={{
                        padding: "3px 10px",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        background: "#f1f5f9",
                        color: "#1e293b",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>

              <div>
                <label className="dispatch-label" style={{ fontSize: 11, color: "#64748b", marginBottom: 4 }}>
                  Assigned Company Scope
                </label>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {comps.map((c) => (
                    <span
                      key={c}
                      style={{
                        padding: "3px 10px",
                        borderRadius: 6,
                        fontSize: 12,
                        fontWeight: 600,
                        background: "#eff6ff",
                        color: "#1d4ed8",
                        border: "1px solid #bfdbfe",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                      }}
                    >
                      <Building2 size={12} />
                      {c}
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ fontSize: 11, color: "#94a3b8", fontStyle: "italic", marginTop: 4 }}>
                Role, department privileges, and multi-company assignments are governed by your Super Admin.
              </div>
            </div>
          </div>

          {/* Card 3: Security & Session */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: 12,
              border: "1px solid var(--line)",
              padding: "24px 26px",
              boxShadow: "0 2px 8px rgba(15, 23, 42, 0.03)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 18, borderBottom: "1px solid #f1f5f9", paddingBottom: 12 }}>
              <KeyRound size={16} style={{ color: "var(--blue)" }} />
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "var(--navy)" }}>Security & Authentication</h3>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label className="dispatch-label" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>Account Status</label>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600, color: "#166534" }}>
                  <CheckCircle2 size={15} style={{ color: "#16a34a" }} />
                  <span>Active & Verified</span>
                </div>
              </div>

              <div>
                <label className="dispatch-label" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>Last Login Session</label>
                <div style={{ fontSize: 13, color: "var(--navy)", display: "flex", alignItems: "center", gap: 6 }}>
                  <Clock size={13} style={{ color: "#64748b" }} />
                  {fmtDateTime(profile?.last_login_at)}
                </div>
              </div>

              <div>
                <label className="dispatch-label" style={{ fontSize: 11, color: "#64748b", marginBottom: 2 }}>Account Created</label>
                <div style={{ fontSize: 13, color: "var(--navy)", display: "flex", alignItems: "center", gap: 6 }}>
                  <Calendar size={13} style={{ color: "#64748b" }} />
                  {fmtDate(profile?.created_at)}
                </div>
              </div>

              <div style={{ paddingTop: 6, borderTop: "1px solid #f1f5f9" }}>
                <button
                  className="outline-btn"
                  style={{ width: "100%", justifyContent: "center" }}
                  onClick={() => {
                    setPwdForm({ current: "", next: "", confirm: "" });
                    setPwdError("");
                    setPwdOpen(true);
                  }}
                >
                  <KeyRound size={14} /> Update Account Password
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {editOpen && (
        <div className="modal-backdrop" style={{ zIndex: 9999 }} data-testid="edit-profile-modal-backdrop">
          <div className="modal" style={{ width: "min(480px, 95%)" }} data-testid="edit-profile-modal">
            <div className="modal-head">
              <b>Edit Personal Profile</b>
              <button onClick={() => setEditOpen(false)}><X size={16} /></button>
            </div>
            <form onSubmit={handleSaveProfile} style={{ padding: 24 }}>
              {editError && (
                <div style={{ padding: "10px 14px", background: "#fef2f2", color: "#dc2626", borderRadius: 8, fontSize: 12.5, marginBottom: 16, border: "1px solid #fecaca" }}>
                  {editError}
                </div>
              )}

              <div className="dispatch-field">
                <label className="dispatch-label">Full Name *</label>
                <input
                  type="text"
                  required
                  className="dispatch-control"
                  data-testid="edit-profile-name-input"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  placeholder="Enter your full name"
                />
              </div>

              <div className="dispatch-field">
                <label className="dispatch-label">Phone Number</label>
                <input
                  type="text"
                  className="dispatch-control"
                  data-testid="edit-profile-phone-input"
                  value={editPhone}
                  onChange={(e) => setEditPhone(e.target.value)}
                  placeholder="+91 98200 12345"
                />
              </div>

              {/* Read-Only Governance Details */}
              <div
                style={{
                  background: "#f8fafc",
                  borderRadius: 8,
                  padding: "12px 14px",
                  fontSize: 12,
                  color: "#475569",
                  marginBottom: 16,
                  border: "1px solid #e2e8f0",
                }}
              >
                <div style={{ fontWeight: 600, color: "var(--navy)", marginBottom: 4 }}>Restricted Administrative Fields</div>
                <div>
                  Your <b>Role</b> ({formatRole(profile?.role)}), <b>Email</b> ({profile?.email}), and assigned <b>Departments</b> cannot be altered via self-service. Contact your Super Admin for structural access changes.
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
                <button
                  type="button"
                  className="outline-btn"
                  onClick={() => setEditOpen(false)}
                  disabled={editBusy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-btn"
                  data-testid="edit-profile-submit-btn"
                  disabled={editBusy}
                >
                  {editBusy ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {pwdOpen && (
        <div className="modal-backdrop" style={{ zIndex: 9999 }} data-testid="change-password-modal-backdrop">
          <div className="modal" style={{ width: "min(460px, 95%)" }} data-testid="change-password-modal">
            <div className="modal-head">
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <KeyRound size={16} style={{ color: "var(--navy)" }} />
                <b>Change Password</b>
              </div>
              <button onClick={() => setPwdOpen(false)}><X size={16} /></button>
            </div>
            <form onSubmit={handlePasswordChange} style={{ padding: 24 }}>
              {pwdError && (
                <div style={{ padding: "10px 14px", background: "#fef2f2", color: "#dc2626", borderRadius: 8, fontSize: 12.5, marginBottom: 16, border: "1px solid #fecaca" }}>
                  {pwdError}
                </div>
              )}

              <div className="dispatch-field">
                <label className="dispatch-label">Current Password *</label>
                <PasswordInput
                  required
                  className="dispatch-control"
                  data-testid="change-password-current-input"
                  placeholder="Enter current password"
                  value={pwdForm.current}
                  onChange={(e) => setPwdForm((f) => ({ ...f, current: e.target.value }))}
                />
              </div>

              <div className="dispatch-field">
                <label className="dispatch-label">New Password * (min 6 characters)</label>
                <PasswordInput
                  required
                  className="dispatch-control"
                  data-testid="change-password-new-input"
                  placeholder="Enter new strong password"
                  value={pwdForm.next}
                  onChange={(e) => setPwdForm((f) => ({ ...f, next: e.target.value }))}
                />
              </div>

              <div className="dispatch-field">
                <label className="dispatch-label">Confirm New Password *</label>
                <PasswordInput
                  required
                  className="dispatch-control"
                  data-testid="change-password-confirm-input"
                  placeholder="Re-enter new password"
                  value={pwdForm.confirm}
                  onChange={(e) => setPwdForm((f) => ({ ...f, confirm: e.target.value }))}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 22 }}>
                <button
                  type="button"
                  className="outline-btn"
                  onClick={() => setPwdOpen(false)}
                  disabled={pwdBusy}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="primary-btn"
                  data-testid="change-password-submit-btn"
                  disabled={pwdBusy}
                >
                  {pwdBusy ? "Updating..." : "Update Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
